import React, { useState } from 'react';

/**
 * A structured editor for the org's JSON config files.
 *
 * These files do not share a shape: regions.json is a bare list, topics.json is an
 * object with a threshold and a list, relationship_types.json nests a list of
 * objects inside an object, outcome_config.json is mostly long strings. Writing a
 * bespoke form per file would be five forms to maintain and would break on the
 * first file nobody anticipated — and there are already four in blob that the page
 * never showed at all.
 *
 * So this renders by shape rather than by filename:
 *   scalar             -> a typed input (checkbox / number / text / textarea)
 *   array of objects   -> a table, columns taken from the union of the rows' keys
 *   array of scalars   -> an editable list
 *   object             -> recurse, one row per key
 *
 * Keys beginning with "_" are comments in these files (_comment, _grounding_note).
 * They are shown read-only rather than hidden, because they carry the reasoning
 * behind the values, and preserved on save either way.
 */

const st = {
  row: { display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0' },
  key: {
    minWidth: '190px', fontSize: '12px', fontWeight: 600, color: '#343a40',
    fontFamily: 'monospace', paddingTop: '5px', wordBreak: 'break-word',
  },
  input: {
    flex: 1, padding: '5px 8px', fontSize: '12px', border: '1px solid #dee2e6',
    borderRadius: '4px', boxSizing: 'border-box', minWidth: 0,
  },
  nested: { borderLeft: '2px solid #e9ecef', paddingLeft: '12px', marginLeft: '4px', flex: 1 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    textAlign: 'left', padding: '5px 6px', borderBottom: '1px solid #dee2e6',
    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#6c757d',
  },
  td: { padding: '3px 4px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' },
  cellInput: {
    width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid #e9ecef',
    borderRadius: '3px', boxSizing: 'border-box',
  },
  btn: {
    padding: '3px 9px', fontSize: '11px', fontWeight: 600, border: '1px solid #dee2e6',
    borderRadius: '4px', cursor: 'pointer', background: '#fff', color: '#495057',
  },
  comment: {
    flex: 1, fontSize: '11px', color: '#868e96', fontStyle: 'italic',
    paddingTop: '5px', lineHeight: 1.5,
  },
};

const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);
// A long string is a paragraph (org_context, a description) and needs room.
const isLongText = v => typeof v === 'string' && (v.length > 80 || v.includes('\n'));

function ScalarInput({ value, onChange }) {
  if (typeof value === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', paddingTop: '4px' }}>
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
        {value ? 'true' : 'false'}
      </label>
    );
  }
  if (typeof value === 'number') {
    return (
      <input
        type="number" step="any" style={st.input} value={value}
        onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    );
  }
  if (isLongText(value)) {
    return (
      <textarea
        style={{ ...st.input, minHeight: '80px', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  return <input type="text" style={st.input} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
}

function ObjectTable({ rows, onChange }) {
  // Union of keys across rows, so a row missing a field still gets a cell for it.
  const columns = [];
  rows.forEach(r => Object.keys(r || {}).forEach(k => { if (!columns.includes(k)) columns.push(k); }));

  const setCell = (i, col, v) => onChange(rows.map((r, ri) => (ri === i ? { ...r, [col]: v } : r)));
  const removeRow = i => onChange(rows.filter((_, ri) => ri !== i));
  const addRow = () => onChange([...rows, Object.fromEntries(columns.map(c => [c, '']))]);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead>
            <tr>
              {columns.map(c => <th key={c} style={st.th}>{c}</th>)}
              <th style={{ ...st.th, width: '34px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map(c => (
                  <td key={c} style={st.td}>
                    {typeof row?.[c] === 'boolean' ? (
                      <input type="checkbox" checked={row[c]} onChange={e => setCell(i, c, e.target.checked)} />
                    ) : isPlainObject(row?.[c]) || Array.isArray(row?.[c]) ? (
                      <span style={{ fontSize: '11px', color: '#868e96' }}>(nested — use raw JSON)</span>
                    ) : (
                      <input
                        style={st.cellInput}
                        value={row?.[c] ?? ''}
                        onChange={e => {
                          const raw = e.target.value;
                          setCell(i, c, typeof row?.[c] === 'number' && raw !== '' && !Number.isNaN(Number(raw))
                            ? Number(raw) : raw);
                        }}
                      />
                    )}
                  </td>
                ))}
                <td style={st.td}>
                  <button style={st.btn} onClick={() => removeRow(i)} title="Remove row">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button style={{ ...st.btn, marginTop: '6px' }} onClick={addRow}>+ Add row</button>
      {rows.length === 0 && (
        <span style={{ fontSize: '11px', color: '#868e96', marginLeft: '8px' }}>
          Empty. Adding a row needs at least one existing row to take its columns from — use raw JSON for the first one.
        </span>
      )}
    </div>
  );
}

function ScalarList({ items, onChange }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
          <input
            style={st.input}
            value={item ?? ''}
            onChange={e => onChange(items.map((v, vi) => (vi === i ? e.target.value : v)))}
          />
          <button style={st.btn} onClick={() => onChange(items.filter((_, vi) => vi !== i))}>×</button>
        </div>
      ))}
      <button style={st.btn} onClick={() => onChange([...items, ''])}>+ Add</button>
    </div>
  );
}

export default function JsonEditor({ value, onChange, depth = 0 }) {
  if (Array.isArray(value)) {
    const objectRows = value.length > 0 && value.every(isPlainObject);
    return objectRows
      ? <ObjectTable rows={value} onChange={onChange} />
      : <ScalarList items={value} onChange={onChange} />;
  }

  if (isPlainObject(value)) {
    return (
      <div style={depth > 0 ? st.nested : { width: '100%' }}>
        {Object.entries(value).map(([key, child]) => {
          const setChild = next => onChange({ ...value, [key]: next });
          const isComment = key.startsWith('_');
          return (
            <div key={key} style={st.row}>
              <div style={{ ...st.key, color: isComment ? '#868e96' : st.key.color }}>{key}</div>
              {isComment
                ? <div style={st.comment}>{String(child)}</div>
                : <JsonEditor value={child} onChange={setChild} depth={depth + 1} />}
            </div>
          );
        })}
        {Object.keys(value).length === 0 && (
          <div style={{ fontSize: '12px', color: '#868e96', padding: '4px 0' }}>
            {depth === 0 ? 'This file is empty.' : 'Nothing set. Use "Edit as text" to add the first entry.'}
          </div>
        )}
      </div>
    );
  }

  return <ScalarInput value={value} onChange={onChange} />;
}
